import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type {
  Category,
  MeasurementPoint,
  Product,
  ProductColour,
  ProductImage,
  ProductMaterial,
  ProductSize,
  ProductTag,
} from '@rosita/shared';
import { SEO } from '../../seo/useSEO';
import { api, extractError } from '../../lib/api';
import { useToast } from '../../components/admin/Toast';
import { slugify } from './product-settings/shared';
import {
  MAX_BATCH_BYTES,
  MAX_FILE_BYTES,
  compressImage,
  formatBytes,
} from '../../lib/imageUpload';
import { sortSizes } from '../../lib/product';
import ProductCompositionEditor from './product-form/ProductCompositionEditor';
import { compositionSum, type CompositionDraft } from './product-form/composition';
import ProductMeasurementsEditor, {
  type MeasurementCells,
} from './product-form/ProductMeasurementsEditor';

interface VariantInput {
  size: string;
  colour: string;
  colourHex: string;
  stock: number;
  priceModifier: number;
}

const emptyVariant: VariantInput = {
  size: '',
  colour: '',
  colourHex: '#000000',
  stock: 0,
  priceModifier: 0,
};

const inputClass =
  'mt-1 block w-full rounded-md border border-admin-border bg-white px-3 py-2 text-sm focus:border-admin-primary focus:outline-none';
const labelClass = 'block text-sm font-medium text-admin-text-soft';
const primaryBtn =
  'rounded-md bg-admin-primary px-4 py-2 text-sm font-medium text-white hover:bg-admin-primary-hover disabled:opacity-50';
const secondaryBtn =
  'rounded-md border border-admin-border bg-white px-3 py-2 text-sm hover:bg-admin-surface-alt';
const iconBtn =
  'flex h-8 w-8 items-center justify-center rounded-md border border-admin-border bg-white text-admin-danger hover:bg-admin-danger hover:text-white';

export default function ProductFormPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [existingSlugs, setExistingSlugs] = useState<Set<string>>(new Set());
  const originalSlugRef = useRef<string>('');
  const [shortDescription, setShortDescription] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState(0);
  const [salePrice, setSalePrice] = useState<number | null>(null);
  const [sku, setSku] = useState('');
  const [weightGrams, setWeightGrams] = useState(0);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [isActive, setIsActive] = useState(true);
  const [highlightAsNew, setHighlightAsNew] = useState(true);
  const [variants, setVariants] = useState<VariantInput[]>([{ ...emptyVariant }]);
  const [images, setImages] = useState<ProductImage[]>([]);
  const [pendingFiles, setPendingFiles] = useState<
    { file: File; url: string; originalName: string; originalSize: number }[]
  >([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [preparingFiles, setPreparingFiles] = useState(false);

  const [categories, setCategories] = useState<Category[]>([]);
  const [sizes, setSizes] = useState<ProductSize[]>([]);
  const [colours, setColours] = useState<ProductColour[]>([]);
  const [materials, setMaterials] = useState<ProductMaterial[]>([]);
  const [measurementPoints, setMeasurementPoints] = useState<MeasurementPoint[]>([]);
  const [tags, setTags] = useState<ProductTag[]>([]);
  const [composition, setComposition] = useState<CompositionDraft[]>([]);
  const [selectedPointIds, setSelectedPointIds] = useState<string[]>([]);
  const [measurementCells, setMeasurementCells] = useState<MeasurementCells>({});
  const [measurementsNote, setMeasurementsNote] = useState('');
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const MAX_IMAGES = 6;

  const pendingFilesRef = useRef(pendingFiles);
  pendingFilesRef.current = pendingFiles;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  useEffect(
    () => () => {
      pendingFilesRef.current.forEach((p) => URL.revokeObjectURL(p.url));
    },
    []
  );

  useEffect(() => {
    void (async () => {
      const [catRes, sizesRes, coloursRes, matsRes, pointsRes, tagsRes, prodsRes] =
        await Promise.all([
          api.get<Category[]>('/admin/categories'),
          api.get<ProductSize[]>('/admin/sizes'),
          api.get<ProductColour[]>('/admin/colours'),
          api.get<ProductMaterial[]>('/admin/materials'),
          api.get<MeasurementPoint[]>('/admin/measurement-points'),
          api.get<ProductTag[]>('/admin/tags'),
          api.get<{ items: Product[] }>('/admin/products?limit=100&status=all'),
        ]);
      setCategories(catRes.data);
      setSizes(sizesRes.data);
      setColours(coloursRes.data);
      setMaterials(matsRes.data);
      setMeasurementPoints(pointsRes.data);
      setTags(tagsRes.data);
      setExistingSlugs(new Set(prodsRes.data.items.map((p) => p.slug)));
    })();
  }, []);

  const loadProduct = async (productId: string): Promise<void> => {
    const response = await api.get<Product>(`/admin/products/${productId}`);
    const p = response.data;
    if (!p) return;
    setName(p.name);
    setSlug(p.slug);
    originalSlugRef.current = p.slug;
    setSlugManuallyEdited(true);
    setShortDescription(p.shortDescription ?? '');
    setDescription(p.description ?? '');
    setPrice(Number(p.price));
    setSalePrice(p.salePrice ? Number(p.salePrice) : null);
    setSku(p.sku ?? '');
    setWeightGrams(p.weightGrams);
    setCategoryId(p.categoryId);
    setIsActive(p.isActive);
    setHighlightAsNew(p.highlightAsNew ?? true);
    setVariants(
      p.variants.length > 0
        ? p.variants.map((v) => ({
            size: v.size,
            colour: v.colour,
            colourHex: v.colourHex ?? '#000000',
            stock: v.stock,
            priceModifier: Number(v.priceModifier),
          }))
        : [{ ...emptyVariant }]
    );
    setImages(p.images);
    setComposition(
      (p.composition ?? []).map((c) => ({
        materialId: c.materialId,
        percentage: c.percentage === null ? '' : String(c.percentage),
      }))
    );
    const rows = p.measurements ?? [];
    setSelectedPointIds(rows.map((r) => r.point.id));
    const cells: MeasurementCells = {};
    for (const r of rows) {
      cells[r.point.id] = Object.fromEntries(
        Object.entries(r.values).map(([size, v]) => [size, v === null ? '' : String(v)])
      );
    }
    setMeasurementCells(cells);
    setMeasurementsNote(p.measurementsNote ?? '');
    setTagIds(p.tagIds ?? []);
  };

  useEffect(() => {
    if (!isEdit || !id) return;
    void loadProduct(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === categoryId) ?? null,
    [categories, categoryId]
  );
  const parentCategoryId = selectedCategory?.parentId ?? selectedCategory?.id ?? null;
  const topLevelCategories = useMemo(
    () =>
      categories.filter(
        (c) => !c.parentId && (c.isActive || c.id === parentCategoryId)
      ),
    [categories, parentCategoryId]
  );
  const availableSubcategories = useMemo(
    () =>
      parentCategoryId
        ? categories.filter(
            (c) =>
              c.parentId === parentCategoryId &&
              (c.isActive || c.id === categoryId)
          )
        : [],
    [categories, parentCategoryId, categoryId]
  );

  // Columns of the measurements grid: the distinct sizes across variants, in
  // taxonomy order (falls back to the storefront's letter-size ordering).
  const gridSizes = useMemo(() => {
    const distinct = Array.from(new Set(variants.map((v) => v.size).filter(Boolean)));
    const order = new Map(sizes.map((s) => [s.label, s.sortOrder]));
    const known = distinct.filter((s) => order.has(s));
    const unknown = distinct.filter((s) => !order.has(s));
    known.sort((a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0));
    return [...known, ...sortSizes(unknown)];
  }, [variants, sizes]);

  const handleParentCategoryChange = (next: string): void => {
    setCategoryId(next || null);
  };
  const handleSubcategoryChange = (next: string): void => {
    setCategoryId(next || parentCategoryId);
  };

  const ensureUniqueSlug = (base: string): string => {
    if (!base) return '';
    if (!existingSlugs.has(base) || base === originalSlugRef.current) return base;
    let n = 2;
    while (
      existingSlugs.has(`${base}-${n}`) &&
      `${base}-${n}` !== originalSlugRef.current
    ) {
      n += 1;
    }
    return `${base}-${n}`;
  };
  const slugCollision =
    slug !== '' &&
    slug !== originalSlugRef.current &&
    existingSlugs.has(slug);

  // Pending images all go up in a single multipart request, so the batch total
  // is what matters — that's the number Nginx measures against
  // client_max_body_size before Node ever sees the request.
  const pendingBytes = pendingFiles.reduce((sum, p) => sum + p.file.size, 0);
  const batchUsedPct = Math.min(100, Math.round((pendingBytes / MAX_BATCH_BYTES) * 100));
  const batchOverLimit = pendingBytes > MAX_BATCH_BYTES;
  const batchNearLimit = !batchOverLimit && batchUsedPct >= 80;

  const addVariant = (): void => setVariants((v) => [...v, { ...emptyVariant }]);
  const removeVariant = (index: number): void =>
    setVariants((v) => (v.length > 1 ? v.filter((_, i) => i !== index) : v));
  const updateVariant = (index: number, patch: Partial<VariantInput>): void =>
    setVariants((v) => v.map((x, i) => (i === index ? { ...x, ...patch } : x)));

  const handleSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (compositionSum(composition) > 100) {
      toast.error('A soma das percentagens da composição não pode exceder 100%.');
      return;
    }
    setSubmitting(true);
    try {
      const measurements = selectedPointIds.flatMap((pointId) =>
        gridSizes.map((size) => {
          const raw = measurementCells[pointId]?.[size] ?? '';
          const parsed = raw === '' ? NaN : Number(raw);
          return { pointId, size, valueCm: Number.isFinite(parsed) ? parsed : null };
        })
      );
      const payload = {
        name,
        slug,
        description: description || null,
        shortDescription: shortDescription || null,
        categoryId,
        price,
        salePrice,
        sku: sku || null,
        weightGrams,
        isActive,
        highlightAsNew,
        variants,
        composition: composition.map((c, i) => ({
          materialId: c.materialId,
          percentage: c.percentage === '' ? null : Number(c.percentage),
          sortOrder: i,
        })),
        measurements,
        measurementsNote: measurementsNote.trim() || null,
        tagIds,
      };
      let productId = id;
      if (isEdit && id) {
        await api.put(`/admin/products/${id}`, payload);
      } else {
        const response = await api.post<{ id: string }>('/admin/products', payload);
        productId = response.data.id;
      }

      if (pendingFiles.length > 0 && productId) {
        if (images.length + pendingFiles.length > MAX_IMAGES) {
          throw new Error(`Máximo ${MAX_IMAGES} imagens por produto`);
        }
        if (batchOverLimit) {
          throw new Error(
            `As imagens somam ${formatBytes(pendingBytes)} e o limite por envio é ${formatBytes(
              MAX_BATCH_BYTES
            )}. Guarda estas imagens em dois passos: remove algumas, guarda, e adiciona as restantes depois.`
          );
        }
        const formData = new FormData();
        pendingFiles.forEach((p) => formData.append('images', p.file));
        await api.post(`/admin/products/${productId}/images`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        pendingFiles.forEach((p) => URL.revokeObjectURL(p.url));
        setPendingFiles([]);
      }

      if (isEdit && productId) {
        await loadProduct(productId);
        toast.success('Produto atualizado com sucesso.');
      } else {
        toast.success('Produto criado com sucesso.');
        navigate('/admin/produtos');
      }
    } catch (err) {
      toast.error(extractError(err, 'Falha ao guardar produto'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileSelect = async (selected: FileList | null): Promise<void> => {
    setFileError(null);
    if (!selected || selected.length === 0) return;
    const incoming = Array.from(selected);
    if (images.length + pendingFiles.length + incoming.length > MAX_IMAGES) {
      setFileError(
        `Máximo ${MAX_IMAGES} imagens. Tens ${images.length + pendingFiles.length}, estás a adicionar ${incoming.length}.`
      );
      return;
    }

    // Downscale before queueing so the size shown in the indicator is the size
    // that actually gets uploaded.
    setPreparingFiles(true);
    try {
      const prepared = await Promise.all(
        incoming.map(async (file) => {
          const compressed = await compressImage(file);
          return {
            file: compressed,
            url: URL.createObjectURL(compressed),
            originalName: file.name,
            originalSize: file.size,
          };
        })
      );
      const tooBig = prepared.filter((p) => p.file.size > MAX_FILE_BYTES);
      if (tooBig.length > 0) {
        tooBig.forEach((p) => URL.revokeObjectURL(p.url));
        const kept = prepared.filter((p) => p.file.size <= MAX_FILE_BYTES);
        setPendingFiles((prev) => [...prev, ...kept]);
        setFileError(
          `${tooBig.map((p) => p.originalName).join(', ')} ${
            tooBig.length === 1 ? 'é demasiado grande' : 'são demasiado grandes'
          } (máximo ${formatBytes(MAX_FILE_BYTES)} por imagem).`
        );
        return;
      }
      setPendingFiles((prev) => [...prev, ...prepared]);
    } finally {
      setPreparingFiles(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith('image/')
    );
    if (dropped.length === 0) {
      setFileError('Apenas ficheiros de imagem são permitidos.');
      return;
    }
    const dt = new DataTransfer();
    dropped.forEach((f) => dt.items.add(f));
    void handleFileSelect(dt.files);
  };

  const removePendingFile = (index: number): void => {
    setPendingFiles((prev) => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed.url);
      return prev.filter((_, i) => i !== index);
    });
    setFileError(null);
  };

  const removeImage = async (imageId: string): Promise<void> => {
    if (!id) return;
    await api.delete(`/admin/products/${id}/images/${imageId}`);
    setImages((imgs) => {
      const remaining = imgs.filter((i) => i.id !== imageId);
      const removed = imgs.find((i) => i.id === imageId);
      if (removed?.isPrimary && remaining[0]) {
        remaining[0] = { ...remaining[0], isPrimary: true };
      }
      return remaining;
    });
  };

  const setPrimary = async (imageId: string): Promise<void> => {
    if (!id) return;
    const previous = images;
    setImages((imgs) => imgs.map((i) => ({ ...i, isPrimary: i.id === imageId })));
    try {
      await api.patch(`/admin/products/${id}/images/${imageId}/primary`);
    } catch (err) {
      setImages(previous);
      toast.error(extractError(err, 'Falha ao definir imagem principal'));
    }
  };

  const moveImage = async (fromIdx: number, toIdx: number): Promise<void> => {
    if (!id) return;
    if (toIdx < 0 || toIdx >= images.length || fromIdx === toIdx) return;
    const previous = images;
    const next = [...images];
    const [moved] = next.splice(fromIdx, 1);
    if (!moved) return;
    next.splice(toIdx, 0, moved);
    setImages(next);
    try {
      await api.patch(`/admin/products/${id}/images/reorder`, {
        order: next.map((i) => i.id),
      });
    } catch (err) {
      setImages(previous);
      toast.error(extractError(err, 'Falha ao reordenar imagens'));
    }
  };

  return (
    <>
      <SEO title={isEdit ? 'Editar produto' : 'Novo produto'} noindex />
      <h1>{isEdit ? 'Editar produto' : 'Novo produto'}</h1>
      <form
        onSubmit={handleSubmit}
        className="mt-6 grid max-w-3xl gap-4 rounded-lg border border-admin-border bg-admin-surface p-6"
      >
        <label className={labelClass}>
          Nome
          <input
            value={name}
            onChange={(e) => {
              const v = e.target.value;
              setName(v);
              if (!slugManuallyEdited) {
                setSlug(ensureUniqueSlug(slugify(v)));
              }
            }}
            required
            className={inputClass}
          />
        </label>
        <label className={labelClass}>
          Slug
          <input
            value={slug}
            onChange={(e) => {
              const v = e.target.value;
              setSlug(v);
              setSlugManuallyEdited(v !== '');
            }}
            required
            className={inputClass}
          />
          {slugCollision ? (
            <span className="mt-1 block text-sm text-admin-danger">
              Já existe um produto com o slug "{slug}".
            </span>
          ) : null}
        </label>
        <label className={labelClass}>
          Descrição curta
          <input
            value={shortDescription}
            onChange={(e) => setShortDescription(e.target.value)}
            maxLength={500}
            className={inputClass}
          />
        </label>
        <label className={labelClass}>
          Descrição
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
            className={inputClass}
          />
        </label>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <label className={labelClass}>
            Preço (€)
            <input
              type="number"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(Number(e.target.value))}
              required
              className={inputClass}
            />
          </label>
          <label className={labelClass}>
            Preço promoção (€)
            <input
              type="number"
              step="0.01"
              value={salePrice ?? ''}
              onChange={(e) =>
                setSalePrice(e.target.value ? Number(e.target.value) : null)
              }
              className={inputClass}
            />
          </label>
          <label className={labelClass}>
            Peso (g)
            <input
              type="number"
              value={weightGrams}
              onChange={(e) => setWeightGrams(Number(e.target.value))}
              className={inputClass}
            />
          </label>
        </div>
        <label className={labelClass}>
          SKU
          <input
            value={sku}
            onChange={(e) => setSku(e.target.value)}
            className={inputClass}
          />
        </label>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <label className={labelClass}>
            Categoria
            <select
              value={parentCategoryId ?? ''}
              onChange={(e) => handleParentCategoryChange(e.target.value)}
              className={inputClass}
            >
              <option value="">—</option>
              {topLevelCategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {!c.isActive ? ' (inativa)' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className={labelClass}>
            Sub-categoria
            <select
              value={selectedCategory?.parentId ? selectedCategory.id : ''}
              onChange={(e) => handleSubcategoryChange(e.target.value)}
              disabled={!parentCategoryId || availableSubcategories.length === 0}
              className={inputClass}
            >
              <option value="">
                {!parentCategoryId
                  ? 'Escolhe uma categoria primeiro'
                  : availableSubcategories.length === 0
                    ? 'Sem sub-categorias'
                    : '—'}
              </option>
              {availableSubcategories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {!c.isActive ? ' (inativa)' : ''}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 rounded border-admin-border accent-admin-primary"
          />
          À venda
        </label>
        <label className="flex flex-wrap items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={highlightAsNew}
            onChange={(e) => setHighlightAsNew(e.target.checked)}
            className="h-4 w-4 rounded border-admin-border accent-admin-primary"
          />
          Destacar como "Novo"
          <span className="text-xs text-admin-muted">
            (remove-se automaticamente após o período configurado)
          </span>
        </label>

        <h2 className="mt-2">Variantes</h2>
        <div className="space-y-3">
          {variants.map((v, i) => (
            <div
              key={i}
              className="grid grid-cols-1 items-end gap-2 rounded-md bg-admin-surface-alt p-3 md:grid-cols-[1fr_1fr_80px_1fr_1fr_auto]"
            >
              <label className={labelClass}>
                Tamanho
                <select
                  value={v.size}
                  onChange={(e) => updateVariant(i, { size: e.target.value })}
                  required
                  className={inputClass}
                >
                  <option value="">—</option>
                  {sizes
                    .filter((s) => s.isActive || s.label === v.size)
                    .map((s) => (
                      <option key={s.id} value={s.label}>
                        {s.label}
                        {!s.isActive ? ' (inativo)' : ''}
                      </option>
                    ))}
                  {v.size && !sizes.some((s) => s.label === v.size) ? (
                    <option value={v.size}>{v.size} (legado)</option>
                  ) : null}
                </select>
              </label>
              <label className={labelClass}>
                Cor
                <select
                  value={v.colour}
                  onChange={(e) => {
                    const name = e.target.value;
                    const colour = colours.find((c) => c.name === name);
                    updateVariant(i, {
                      colour: name,
                      colourHex: colour?.hex ?? v.colourHex,
                    });
                  }}
                  required
                  className={inputClass}
                >
                  <option value="">—</option>
                  {colours
                    .filter((c) => c.isActive || c.name === v.colour)
                    .map((c) => (
                      <option key={c.id} value={c.name}>
                        {c.name}
                        {!c.isActive ? ' (inativa)' : ''}
                      </option>
                    ))}
                  {v.colour && !colours.some((c) => c.name === v.colour) ? (
                    <option value={v.colour}>{v.colour} (legado)</option>
                  ) : null}
                </select>
              </label>
              <label className={labelClass}>
                Hex
                <div className="mt-1 flex items-center gap-2">
                  <span
                    aria-hidden
                    className="inline-block h-8 w-8 rounded border border-admin-border"
                    style={{ backgroundColor: v.colourHex }}
                  />
                  <span className="text-xs text-admin-muted">{v.colourHex}</span>
                </div>
              </label>
              <label className={labelClass}>
                Stock
                <input
                  type="number"
                  value={v.stock}
                  onChange={(e) =>
                    updateVariant(i, { stock: Number(e.target.value) })
                  }
                  required
                  className={inputClass}
                />
              </label>
              <label className={labelClass}>
                +€
                <input
                  type="number"
                  step="0.01"
                  value={v.priceModifier}
                  onChange={(e) =>
                    updateVariant(i, { priceModifier: Number(e.target.value) })
                  }
                  className={inputClass}
                />
              </label>
              <button
                type="button"
                onClick={() => removeVariant(i)}
                aria-label="Remover variante"
                className={iconBtn}
              >
                ×
              </button>
            </div>
          ))}
        </div>
        <button type="button" onClick={addVariant} className={secondaryBtn}>
          + Adicionar variante
        </button>

        <h2 className="mt-2">Medidas</h2>
        <ProductMeasurementsEditor
          points={measurementPoints}
          sizes={gridSizes}
          selectedPointIds={selectedPointIds}
          onSelectedChange={setSelectedPointIds}
          cells={measurementCells}
          onCellsChange={setMeasurementCells}
          note={measurementsNote}
          onNoteChange={setMeasurementsNote}
        />

        <h2 className="mt-2">Composição</h2>
        <ProductCompositionEditor
          materials={materials}
          value={composition}
          onChange={setComposition}
        />

        <h2 className="mt-2">Tags</h2>
        {tags.length === 0 ? (
          <p className="text-sm text-admin-muted">
            Sem tags. Cria-as em Produtos &rsaquo; Configurações &rsaquo; Tags.
          </p>
        ) : (
          <>
            <p className="-mt-2 text-sm text-admin-muted">
              Clica para selecionar as tags que se aplicam a este produto.{' '}
              <span className="text-admin-text-soft">
                {tagIds.length} de{' '}
                {tags.filter((t) => t.isActive || tagIds.includes(t.id)).length} selecionada(s).
              </span>
            </p>
            <div className="flex flex-wrap gap-2">
              {tags
                .filter((t) => t.isActive || tagIds.includes(t.id))
                .map((t) => {
                  const selected = tagIds.includes(t.id);
                  return (
                    <label
                      key={t.id}
                      aria-pressed={selected}
                      className={`inline-flex cursor-pointer select-none items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors ${
                        selected
                          ? 'border-admin-primary bg-admin-primary text-white shadow-sm'
                          : 'border-dashed border-admin-border bg-white text-admin-text-soft hover:border-admin-primary hover:text-admin-primary'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={selected}
                        onChange={(e) => {
                          setTagIds((ids) =>
                            e.target.checked ? [...ids, t.id] : ids.filter((x) => x !== t.id)
                          );
                        }}
                      />
                      <span aria-hidden="true" className="text-xs leading-none">
                        {selected ? '✓' : '+'}
                      </span>
                      {t.name}
                    </label>
                  );
                })}
            </div>
          </>
        )}

        <h2 className="mt-2">Imagens</h2>
        {images.length > 0 || pendingFiles.length > 0 ? (
          <div className="flex flex-wrap gap-3">
            {images.map((img, i) => (
              <div
                key={img.id}
                className="flex flex-col gap-1 rounded-md border border-admin-border bg-white p-2"
              >
                <div className="relative">
                  <img
                    src={img.thumbUrl}
                    alt={img.altText ?? ''}
                    className="h-32 w-24 rounded object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      void setPrimary(img.id);
                    }}
                    aria-label={
                      img.isPrimary ? 'Imagem principal' : 'Definir como principal'
                    }
                    title={img.isPrimary ? 'Imagem principal' : 'Definir como principal'}
                    className={`absolute left-1 top-1 flex h-7 w-7 items-center justify-center rounded-full text-base shadow ${
                      img.isPrimary
                        ? 'bg-amber-400 text-white'
                        : 'bg-white/90 text-admin-text-soft hover:bg-amber-400 hover:text-white'
                    }`}
                  >
                    {img.isPrimary ? '★' : '☆'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      void removeImage(img.id);
                    }}
                    aria-label="Remover imagem"
                    className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-admin-danger text-xs text-white hover:opacity-90"
                  >
                    ×
                  </button>
                </div>
                <div className="flex items-center justify-between gap-1 px-1 text-xs">
                  <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-admin-surface-alt px-1 text-admin-text-soft">
                    {i + 1}
                  </span>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => {
                        void moveImage(i, i - 1);
                      }}
                      disabled={i === 0}
                      aria-label="Mover para cima"
                      className="flex h-6 w-6 items-center justify-center rounded border border-admin-border bg-white disabled:opacity-30"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        void moveImage(i, i + 1);
                      }}
                      disabled={i === images.length - 1}
                      aria-label="Mover para baixo"
                      className="flex h-6 w-6 items-center justify-center rounded border border-admin-border bg-white disabled:opacity-30"
                    >
                      ↓
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {pendingFiles.map((p, i) => (
              <div
                key={p.url}
                className="flex flex-col gap-1 rounded-md border border-dashed border-admin-primary bg-white p-2"
              >
                <div className="relative">
                  <img
                    src={p.url}
                    alt={p.file.name}
                    className="h-32 w-24 rounded object-cover"
                  />
                  <span className="absolute left-1 top-1 rounded bg-admin-primary px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white shadow">
                    Novo
                  </span>
                  <button
                    type="button"
                    onClick={() => removePendingFile(i)}
                    aria-label="Remover imagem pendente"
                    className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-admin-danger text-xs text-white hover:opacity-90"
                  >
                    ×
                  </button>
                </div>
                <span
                  className="truncate px-1 text-xs text-admin-text-soft"
                  title={p.originalName}
                >
                  {p.originalName}
                </span>
                <span className="px-1 text-[10px] text-admin-muted">
                  {formatBytes(p.file.size)}
                  {p.file.size < p.originalSize
                    ? ` (era ${formatBytes(p.originalSize)})`
                    : ''}
                </span>
              </div>
            ))}
          </div>
        ) : null}
        {pendingFiles.length > 0 ? (
          <div
            className={`rounded-md border px-3 py-2 ${
              batchOverLimit
                ? 'border-admin-danger bg-admin-danger/5'
                : batchNearLimit
                ? 'border-amber-400 bg-amber-50'
                : 'border-admin-border bg-admin-surface-alt'
            }`}
          >
            <div className="flex items-baseline justify-between gap-2 text-xs">
              <span className="font-medium text-admin-text-soft">
                Tamanho do envio
              </span>
              <span
                className={
                  batchOverLimit
                    ? 'font-semibold text-admin-danger'
                    : 'text-admin-text-soft'
                }
              >
                {formatBytes(pendingBytes)} de {formatBytes(MAX_BATCH_BYTES)}
              </span>
            </div>
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={batchUsedPct}
              aria-label="Tamanho total das imagens a enviar"
              className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-admin-border"
            >
              <div
                className={`h-full rounded-full transition-all ${
                  batchOverLimit
                    ? 'bg-admin-danger'
                    : batchNearLimit
                    ? 'bg-amber-500'
                    : 'bg-admin-primary'
                }`}
                style={{ width: `${Math.max(batchUsedPct, 2)}%` }}
              />
            </div>
            <p className="mt-1.5 text-xs text-admin-muted">
              {batchOverLimit
                ? 'As imagens são demasiado grandes para um só envio. Remove uma ou duas, guarda o produto, e adiciona as restantes depois.'
                : batchNearLimit
                ? 'Estás perto do limite. Se adicionares mais imagens, guarda-as em dois passos.'
                : 'As imagens são reduzidas automaticamente antes de serem enviadas.'}
            </p>
          </div>
        ) : null}

        {images.length + pendingFiles.length < MAX_IMAGES ? (
          <div>
            <p className={labelClass}>
              Adicionar imagens ({images.length + pendingFiles.length}/{MAX_IMAGES})
            </p>
            <div
              onDragEnter={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragging(false);
              }}
              onDrop={handleDrop}
              className={`mt-1 flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-4 py-8 text-center transition-colors ${
                isDragging
                  ? 'border-admin-primary bg-admin-cream'
                  : 'border-admin-border bg-admin-surface-alt'
              }`}
            >
              <p className="text-sm text-admin-text-soft">
                Arrasta imagens para aqui ou
              </p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={preparingFiles}
                className={secondaryBtn}
              >
                {preparingFiles ? 'A preparar imagens…' : 'Carregar imagens'}
              </button>
              <p className="text-xs text-admin-muted">PNG, JPG, WEBP</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => {
                  void handleFileSelect(e.target.files);
                  e.target.value = '';
                }}
                className="sr-only"
              />
            </div>
            {fileError ? (
              <p className="mt-1 text-sm text-admin-danger">{fileError}</p>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-admin-text-soft">
            Limite de {MAX_IMAGES} imagens atingido. Remove uma para adicionar outra.
          </p>
        )}

        <button
          type="submit"
          disabled={submitting || slugCollision || preparingFiles || batchOverLimit}
          className={primaryBtn}
        >
          {submitting
            ? isEdit
              ? 'A atualizar…'
              : 'A criar…'
            : isEdit
              ? 'Atualizar produto'
              : 'Criar produto'}
        </button>
      </form>
    </>
  );
}
